import type { PaymentStatus } from './orders';

/**
 * Payment rules shared by the POS, the tests and the server (which re-validates everything).
 * Phase 4 (ADR-019): a tax-neutral ledger of captured tenders and immutable reversals. Nothing
 * here knows about VAT, official receipts or discounts; those arrive with the compliance phase.
 */

export const PAYMENT_METHOD_KINDS = [
  'cash',
  'ewallet',
  'card',
  'bank_transfer',
  'complimentary',
  'other',
  'online',
] as const;
export type PaymentMethodKind = (typeof PAYMENT_METHOD_KINDS)[number];

/**
 * Rows the owner can create today. `online` waits for a provider integration (Phase 10). `complimentary` is no
 * longer a way of paying (ADR-031, D4): a comp is the owner's write-off. Methods of that kind created before stay
 * as history; they are never captured into.
 */
export const CREATABLE_METHOD_KINDS = PAYMENT_METHOD_KINDS.filter(
  (k) => k !== 'online' && k !== 'complimentary',
);

/** A method the counter takes money by: never `complimentary` (ADR-031, D4). */
export function isCapturableMethodKind(kind: PaymentMethodKind): boolean {
  return kind !== 'complimentary';
}

export const PAYMENT_ROW_STATUSES = ['pending', 'captured', 'failed'] as const;
export type PaymentRowStatus = (typeof PAYMENT_ROW_STATUSES)[number];

/** Operational terms only (owner correction 5): no legal meaning is attached to either kind. */
export const REVERSAL_KINDS = ['void', 'refund'] as const;
export type ReversalKind = (typeof REVERSAL_KINDS)[number];

export const RECEIPT_KINDS = ['sale', 'void', 'refund'] as const;
export type ReceiptKind = (typeof RECEIPT_KINDS)[number];

export const MAX_TENDERS_PER_CAPTURE = 5;
export const MAX_REFERENCE_LENGTH = 64;

/* ── Statuses ─────────────────────────────────────────────────────────────────────────────── */

export interface MoneyTotals {
  totalMinor: number;
  /** Captured tenders minus their void reversals (a void makes that money vanish). */
  paidMinor: number;
  /** Sum of refund reversals (money given back). */
  refundedMinor: number;
  /** Written off and still standing (ADR-031): owed no more, but never money received. */
  writtenOffMinor?: number;
}

/**
 * Mirror of `app.derive_payment_status`: voids make money vanish, refunds mark it returned. The
 * current settlement is the net kept (paid minus refunded): once it covers the total the order is
 * paid, whatever refunds it saw before (they stay in refundedMinor and the ledger). With something
 * written off (ADR-031) that, with the net kept, covers the total, the order is written off.
 */
export function derivePaymentStatus(t: MoneyTotals): PaymentStatus {
  const writtenOff = t.writtenOffMinor ?? 0;
  if (writtenOff > 0 && t.paidMinor - t.refundedMinor + writtenOff >= t.totalMinor)
    return 'written_off';
  if (t.paidMinor <= 0) return 'unpaid';
  const net = t.paidMinor - t.refundedMinor;
  if (net > 0 && net >= t.totalMinor) return 'paid';
  if (t.refundedMinor <= 0) return 'partially_paid';
  return net <= 0 ? 'refunded' : 'partially_refunded';
}

/** What is still owed, net of what is written off; never negative (an over-paid order after a line removal shows 0). */
export function remainingMinor(t: MoneyTotals): number {
  return Math.max(0, t.totalMinor - (t.paidMinor - t.refundedMinor) - (t.writtenOffMinor ?? 0));
}

/** What could still be given back. */
export function refundableMinor(t: Pick<MoneyTotals, 'paidMinor' | 'refundedMinor'>): number {
  return Math.max(0, t.paidMinor - t.refundedMinor);
}

/* ── What a tender can still give back ────────────────────────────────────────────────────── */

export interface TenderRow {
  id: string;
  amount_minor: number;
  status: PaymentRowStatus;
}
export interface ReversalRow {
  /** The tender reversed. Refunds recorded before the reversal-integrity fix name none. */
  payment_id: string | null;
  kind: ReversalKind;
  amount_minor: number;
  /** A cancelled refund (ADR-028): it stays in the history and gives nothing back. */
  cancelled?: boolean;
}

/** The reversals that stand: a cancelled one is absent from every money sum. */
function standing(reversals: readonly ReversalRow[]): ReversalRow[] {
  return reversals.filter((r) => !r.cancelled);
}

/** Captured on the order minus every reversal on it: the most the order can still give back. */
function orderCapMinor(tenders: readonly TenderRow[], reversals: readonly ReversalRow[]): number {
  const captured = tenders
    .filter((t) => t.status === 'captured')
    .reduce((s, t) => s + t.amount_minor, 0);
  return captured - standing(reversals).reduce((s, r) => s + r.amount_minor, 0);
}

function ownLeftMinor(t: TenderRow, reversals: readonly ReversalRow[]): number {
  return (
    t.amount_minor -
    standing(reversals)
      .filter((r) => r.payment_id === t.id)
      .reduce((s, r) => s + r.amount_minor, 0)
  );
}

/**
 * Mirror of `app.payment_reversible_minor`: per captured tender, its amount minus every refund
 * and void already linked to it, capped by what the whole order can still give back (the cap
 * only bites for older refunds that name no tender). A void reverses exactly this much; a refund
 * from the tender can take at most this much. Never negative.
 */
export function reversibleByPayment(
  tenders: readonly TenderRow[],
  reversals: readonly ReversalRow[],
): Map<string, number> {
  const cap = orderCapMinor(tenders, reversals);
  return new Map(
    tenders.map((t) => [
      t.id,
      t.status === 'captured' ? Math.max(0, Math.min(ownLeftMinor(t, reversals), cap)) : 0,
    ]),
  );
}

/** Mirror of `app.order_reversible_minor`: what the order can still give back across tenders. */
export function orderReversibleMinor(
  tenders: readonly TenderRow[],
  reversals: readonly ReversalRow[],
): number {
  const own = tenders
    .filter((t) => t.status === 'captured')
    .reduce((s, t) => s + Math.max(0, ownLeftMinor(t, reversals)), 0);
  return Math.max(0, Math.min(own, orderCapMinor(tenders, reversals)));
}

/* ── Tenders ──────────────────────────────────────────────────────────────────────────────── */

export interface TenderInput {
  /** Client-generated UUID so a retried capture cannot double-record (Phase 0 section J.2). */
  id?: string;
  payment_method_id: string;
  amount_minor: number;
  /** Cash only: what the customer handed over; change is the difference. */
  tendered_minor?: number | null;
  /** Reference number when the method requires one (GCash, bank transfer). */
  reference?: string | null;
  note?: string | null;
  /** A reference used before in the business: why it is taken again (D5, ADR-032). */
  duplicate_reason?: string | null;
}

export interface MethodRule {
  id: string;
  kind: PaymentMethodKind;
  enabled: boolean;
  requires_reference: boolean;
}

export function changeForTender(t: TenderInput, kind: PaymentMethodKind): number {
  if (kind !== 'cash' || t.tendered_minor == null) return 0;
  return Math.max(0, t.tendered_minor - t.amount_minor);
}

const isMinor = (n: unknown): n is number =>
  typeof n === 'number' && Number.isInteger(n) && Number.isFinite(n);

/**
 * Validate a capture before it is sent. Returns the first problem as a sentence, or null. The
 * server repeats every check; this keeps the checkout sheet honest without a round trip.
 */
export function validateTenders(
  tenders: readonly TenderInput[],
  ctx: {
    remainingMinor: number;
    methods: readonly MethodRule[];
    /** Whether a total below the remaining balance is acceptable (deposit). */
    partialAllowed: boolean;
  },
): string | null {
  if (tenders.length === 0) return 'Add at least one payment';
  if (tenders.length > MAX_TENDERS_PER_CAPTURE)
    return `At most ${MAX_TENDERS_PER_CAPTURE} payments at once`;
  if (ctx.remainingMinor <= 0) return 'Nothing left to pay';
  let sum = 0;
  for (const t of tenders) {
    const m = ctx.methods.find((x) => x.id === t.payment_method_id);
    if (!m) return 'Choose a payment method';
    if (!m.enabled) return 'That payment method is disabled';
    if (!isMinor(t.amount_minor) || t.amount_minor <= 0) return 'Amount must be more than zero';
    if (m.kind === 'cash') {
      if (
        t.tendered_minor != null &&
        (!isMinor(t.tendered_minor) || t.tendered_minor < t.amount_minor)
      )
        return 'Cash tendered must cover the amount';
    } else if (t.tendered_minor != null && t.tendered_minor !== t.amount_minor) {
      return 'Only cash can be over-tendered';
    }
    const ref = (t.reference ?? '').trim();
    if (m.requires_reference && !ref) return 'A reference number is required for this method';
    if (ref.length > MAX_REFERENCE_LENGTH)
      return `Reference must be ${MAX_REFERENCE_LENGTH} characters or fewer`;
    if (t.note && t.note.length > 200) return 'Note must be 200 characters or fewer';
    sum += t.amount_minor;
  }
  if (sum > ctx.remainingMinor) return 'Payments exceed the remaining balance';
  if (sum < ctx.remainingMinor && !ctx.partialAllowed)
    return 'Payments must cover the remaining balance';
  return null;
}

/** Bills a Philippine cashier reaches for, in minor units. */
export const CASH_DENOMINATIONS_MINOR = [2000, 5000, 10000, 20000, 50000, 100000] as const;

/**
 * Quick-tender amounts for the cash pad: the exact amount, then the next round bill values
 * above it, de-duplicated and capped to four suggestions. `[34000] → [34000, 50000, 100000]`.
 */
export function cashSuggestions(remainingMinor: number): number[] {
  if (remainingMinor <= 0) return [];
  const out = [remainingMinor];
  for (const d of CASH_DENOMINATIONS_MINOR) {
    const candidate = Math.ceil(remainingMinor / d) * d;
    if (candidate > remainingMinor && !out.includes(candidate)) out.push(candidate);
    if (out.length >= 4) break;
  }
  return out;
}

/* ── Refunds ──────────────────────────────────────────────────────────────────────────────── */

export interface RefundLineInput {
  order_item_id: string;
  qty: number;
  amount_minor: number;
}

export function validateRefund(
  input: { amount_minor: number; reason: string; lines?: readonly RefundLineInput[] },
  ctx: { refundableMinor: number },
): string | null {
  if (!isMinor(input.amount_minor) || input.amount_minor <= 0)
    return 'Refund amount must be more than zero';
  if (input.amount_minor > ctx.refundableMinor) return 'Refund exceeds what was paid';
  if (!input.reason.trim()) return 'A reason is required';
  if (input.reason.length > 200) return 'Reason must be 200 characters or fewer';
  if (input.lines && input.lines.length > 0) {
    let sum = 0;
    for (const l of input.lines) {
      if (!Number.isInteger(l.qty) || l.qty < 1) return 'Line quantity must be 1 or more';
      if (!isMinor(l.amount_minor) || l.amount_minor < 0) return 'Line amount cannot be negative';
      sum += l.amount_minor;
    }
    if (sum !== input.amount_minor) return 'Line amounts must add up to the refund amount';
  }
  return null;
}

/* ── Internal payment slip (owner decision 2: internal only, no fiscal wording) ───────────── */

export interface SlipLine {
  qty: number;
  name: string;
  detail: string | null;
  line_total_minor: number;
  status: 'active' | 'voided';
}
export interface SlipTender {
  method: string;
  amount_minor: number;
  tendered_minor: number | null;
  change_minor: number;
  reference: string | null;
}
export interface SlipReversal {
  /** Present on slips issued from migration …003200 on. */
  id?: string;
  kind: ReversalKind;
  method: string;
  amount_minor: number;
  reason: string;
}
/** One tender a slip's own reversal took money back from. */
export interface SlipReversalPart {
  reversal_id: string;
  payment_id: string | null;
  /** How the money went back; differs from tender_method on a refund override. */
  method: string;
  tender_method: string | null;
  amount_minor: number;
  method_override: boolean;
}
/** What a void or refund slip was issued for: the whole amount and, from …003200, each tender. */
export interface SlipReversalHead {
  id: string;
  kind: ReversalKind;
  amount_minor: number;
  method: string;
  reason: string;
  parts?: SlipReversalPart[];
}
/** Everything a slip shows, frozen at issue time. Built by the server, rendered by the app. */
export interface SlipSnapshot {
  label: 'INTERNAL PAYMENT SLIP';
  official: false;
  kind: ReceiptKind;
  display_number: string;
  business_name: string;
  location_name: string;
  terminal_code: string;
  order_number: string;
  order_type: string;
  table_name: string | null;
  customer_name: string | null;
  issued_at: string;
  issued_by: string | null;
  currency: string;
  lines: SlipLine[];
  subtotal_minor: number;
  total_minor: number;
  tenders: SlipTender[];
  reversals: SlipReversal[];
  paid_minor: number;
  refunded_minor: number;
  /** Paid minus refunded (from …003200; older slips leave it out). */
  net_minor?: number;
  change_minor: number;
  /** The reversal a void or refund slip was issued for; null on sale slips. */
  reversal?: SlipReversalHead | null;
  tax: { regime: 'neutral' };
  /** ADR-031: what is written off and still stands, and each write-off row; only on a slip of an order with any. */
  written_off_minor?: number;
  write_offs?: {
    id: string;
    kind: 'walkout' | 'staff_meal' | 'comp' | 'reinstated';
    amount_minor: number;
    reinstates_id: string | null;
  }[];
  /** ADR-032: each served item returned on the order; only on a slip of an order with any. */
  returns?: {
    id: string;
    line: string;
    qty: number;
    amount_minor: number;
    refunded_minor: number;
    reason: string;
  }[];
}

/**
 * Split a slip's reversals into what this slip reversed (each tender, when the slip carries its
 * parts) and the rest of the order's history. Slips issued before the parts existed have no own
 * list: everything stays in the history, as it was shown when they were issued.
 */
export function slipReversalView(s: Pick<SlipSnapshot, 'reversal' | 'reversals'>): {
  own: {
    kind: ReversalKind;
    reason: string;
    amount_minor: number;
    parts: SlipReversalPart[];
  } | null;
  history: SlipReversal[];
} {
  const parts = s.reversal?.parts ?? [];
  if (!s.reversal || parts.length === 0) return { own: null, history: s.reversals };
  const ids = new Set(parts.map((p) => p.reversal_id));
  return {
    own: {
      kind: s.reversal.kind,
      reason: s.reversal.reason,
      amount_minor: parts.reduce((sum, p) => sum + p.amount_minor, 0),
      parts,
    },
    history: s.reversals.filter((r) => !r.id || !ids.has(r.id)),
  };
}
